import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DocumentData,
  DocumentReference,
  QueryDocumentSnapshot,
  Timestamp,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "@/firebase";

interface MailTemplate {
  id: string;
  title: string;
  subject?: string;
  body?: string;
  sign?: string;
  attach?: string;
}

interface OnlineGuideItem {
  id: string;
  orderRef: DocumentReference;
  orderData: Record<string, any>;
  createdAt?: Timestamp | null;
  orderNumber: string;
  userName: string;
  userEmail: string;
  shikakuName: string;
  totalAmount: number | string;
  paymentMethod: string;
  course: string;
  hstatus: string;
  oninfo: string;
  userData: Record<string, any> | null;
  shikakuData: Record<string, any> | null;
}

const PAGE_SIZE = 30;
const QUERY_BATCH_SIZE = 60;
const START_DATE = new Date(2023, 1, 1, 0, 0, 0);

const formatDate = (value?: Timestamp | string | null) => {
  if (!value) return "-";
  if (value instanceof Timestamp) {
    return value.toDate().toLocaleString("ja-JP");
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("ja-JP");
};

const formatCurrency = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return numeric.toLocaleString("ja-JP");
};

const normalizeString = (value?: string | null) => value?.trim().toLowerCase() ?? "";

const isBlankField = (value: unknown) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
};

const isOnlineGuideEligible = (data: Record<string, any>) => {
  const hstatusEmpty = isBlankField(data.Hstatus);
  const oninfoDone = normalizeString(data.oninfo) === "done";
  return hstatusEmpty && !oninfoDone;
};

const applyTemplateVariables = (templateText: string, context: Record<string, any>) =>
  templateText.replace(/{{\s*([\w.]+)\s*}}/g, (_match, key) => {
    const pathParts = String(key).split(".");
    let current: any = context;
    for (const part of pathParts) {
      if (current === null || current === undefined) return "";
      if (typeof current !== "object") return "";
      current = Object.prototype.hasOwnProperty.call(current, part) ? current[part] : "";
    }
    if (current === null || current === undefined) return "";
    return String(current);
  });

export default function OnlineGuidePage() {
  const [items, setItems] = useState<OnlineGuideItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const pageCursorsRef = useRef<QueryDocumentSnapshot<DocumentData>[]>([]);

  const loadTemplates = useCallback(async () => {
    try {
      const templateSnapshot = await getDocs(
        query(collection(db, "mail_templates"), orderBy("sortNo", "asc"))
      );
      const templateItems = templateSnapshot.docs.map((docSnap) => {
        const data = docSnap.data() as Omit<MailTemplate, "id">;
        return {
          id: docSnap.id,
          title: data.title || docSnap.id,
          subject: data.subject,
          body: data.body,
          sign: data.sign,
          attach: data.attach,
        };
      });
      setTemplates(templateItems);
    } catch (templateError) {
      console.error("Failed to load mail templates", templateError);
    }
  }, []);

  const fetchOrderItem = useCallback(async (orderDoc: QueryDocumentSnapshot<DocumentData>) => {
    const orderData = orderDoc.data() as Record<string, any>;
    if (!isOnlineGuideEligible(orderData)) return null;

    const userRef = orderData.UserRef || orderData.userId;
    let userData: Record<string, any> | null = null;
    let userName = "-";
    let userEmail = "-";

    if (userRef) {
      try {
        const userDocRef = userRef.path ? userRef : doc(db, "User", userRef);
        const userSnapshot = await getDoc(userDocRef);
        if (userSnapshot.exists()) {
          userData = userSnapshot.data() as Record<string, any>;
          userName = userData.display_name || userData.display_Name || userSnapshot.id;
          userEmail = userData.email || "-";
        }
      } catch (userError) {
        console.error("Failed to load user", userError);
      }
    }

    let shikakuName = "-";
    let shikakuData: Record<string, any> | null = null;
    if (orderData.shikakuId) {
      try {
        const shikakuRef = orderData.shikakuId.path
          ? orderData.shikakuId
          : doc(db, "Shikaku", orderData.shikakuId);
        const shikakuSnapshot = await getDoc(shikakuRef);
        if (shikakuSnapshot.exists()) {
          shikakuData = shikakuSnapshot.data() as Record<string, any>;
          shikakuName = shikakuData.ShikakuName || "-";
        }
      } catch (shikakuError) {
        console.error("Failed to load shikaku", shikakuError);
      }
    }

    return {
      id: orderDoc.id,
      orderRef: orderDoc.ref,
      orderData: { ...orderData, id: orderDoc.id },
      createdAt: orderData.createdAt ?? null,
      orderNumber: orderData.orderNumber || "-",
      userName,
      userEmail,
      shikakuName,
      totalAmount: orderData.totalAmount ?? "-",
      paymentMethod: orderData.paymentMethod || "-",
      course: orderData.course ? String(orderData.course) : "-",
      hstatus: orderData.Hstatus || "",
      oninfo: orderData.oninfo || "",
      userData: userData ? { ...userData, display_name: userName, email: userEmail } : null,
      shikakuData,
    } satisfies OnlineGuideItem;
  }, []);

  const fetchOrders = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setError(null);
      try {
        const baseQuery = [
          collection(db, "Order"),
          where("status", "==", "active"),
          where("createdAt", ">=", Timestamp.fromDate(START_DATE)),
          orderBy("createdAt", "desc"),
        ];

        const cursor = targetPage > 1 ? pageCursorsRef.current[targetPage - 2] : undefined;
        let nextCursor = cursor;
        let collected: OnlineGuideItem[] = [];
        let exhausted = false;
        let reachedPageSize = false;

        while (collected.length < PAGE_SIZE && !exhausted) {
          const snapshot = nextCursor
            ? await getDocs(query(...baseQuery, startAfter(nextCursor), limit(QUERY_BATCH_SIZE)))
            : await getDocs(query(...baseQuery, limit(QUERY_BATCH_SIZE)));

          if (snapshot.empty) {
            exhausted = true;
            break;
          }

          for (const docSnap of snapshot.docs) {
            nextCursor = docSnap;
            const item = await fetchOrderItem(docSnap);
            if (item) {
              collected.push(item);
            }
            if (collected.length >= PAGE_SIZE) {
              reachedPageSize = true;
              break;
            }
          }

          if (!reachedPageSize && snapshot.docs.length < QUERY_BATCH_SIZE) {
            exhausted = true;
          }
          if (reachedPageSize) {
            break;
          }
        }

        setItems(collected);
        setHasNextPage(reachedPageSize || !exhausted);
        setSelectedIds(new Set());
        setPage(targetPage);
        if (nextCursor) {
          const next = [...pageCursorsRef.current];
          next[targetPage - 1] = nextCursor;
          pageCursorsRef.current = next;
        }
      } catch (fetchError: any) {
        console.error("Failed to load online guide orders", fetchError);
        setError("オンライン案内リストの取得に失敗しました。");
      } finally {
        setLoading(false);
      }
    },
    [fetchOrderItem]
  );

  useEffect(() => {
    fetchOrders(1);
  }, [fetchOrders]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const allSelected = useMemo(
    () => items.length > 0 && items.every((item) => selectedIds.has(item.id)),
    [items, selectedIds]
  );

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(items.map((item) => item.id)));
  };

  const toggleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSendSelected = async () => {
    if (!selectedTemplateId) {
      alert("メールテンプレートを選択してください。");
      return;
    }
    if (selectedIds.size === 0) {
      alert("送信対象を選択してください。");
      return;
    }

    const template = templates.find((item) => item.id === selectedTemplateId);
    if (!template) {
      alert("テンプレートの取得に失敗しました。");
      return;
    }

    if (!confirm("選択した宛先にメールを送信しますか?")) return;

    setSending(true);
    try {
      let sentCount = 0;
      for (const item of items) {
        if (!selectedIds.has(item.id)) continue;
        if (!item.userEmail || item.userEmail === "-") continue;

        const templateContext = {
          order: item.orderData,
          user: item.userData ?? { display_name: item.userName, email: item.userEmail },
          shikaku: item.shikakuData ?? { ShikakuName: item.shikakuName },
        };

        const subject = applyTemplateVariables(template.subject || template.title || "", templateContext);
        const body = applyTemplateVariables(template.body || "", templateContext);
        const sign = applyTemplateVariables(template.sign || "", templateContext);
        const attach = applyTemplateVariables(template.attach || "", templateContext);
        const bodyWithSignature = sign ? `${body}\n\n${sign}` : body;

        const mailDoc = await addDoc(collection(db, "mail"), {
          customerEmail: item.userEmail,
          subject: subject || "(no subject)",
          status: "waiting",
          spamLevel: 0,
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
        });

        await addDoc(collection(db, "mail_messages"), {
          mailId: mailDoc,
          direction: "out",
          status: "waiting",
          from: auth.currentUser?.email || "info@lear-caree.jp",
          to: item.userEmail,
          subject: subject || "(no subject)",
          body: bodyWithSignature,
          attach,
          sign,
          createdAt: serverTimestamp(),
        });

        await updateDoc(item.orderRef, {
          oninfo: "done",
        });
        sentCount += 1;
      }

      alert(`送信処理を開始しました。\n送信開始: ${sentCount}件`);
      await fetchOrders(page);
    } catch (sendError: any) {
      console.error("Failed to send online guide mails", sendError);
      alert(sendError?.message || "送信に失敗しました。");
    } finally {
      setSending(false);
      setSelectedIds(new Set());
    }
  };

  const handlePrevPage = () => {
    if (page <= 1) return;
    fetchOrders(page - 1);
  };

  const handleNextPage = () => {
    if (!hasNextPage) return;
    fetchOrders(page + 1);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">オンライン案内</h2>
          <p className="text-sm text-gray-500">Orderコレクションから対象データを抽出しています。</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedTemplateId}
            onChange={(event) => setSelectedTemplateId(event.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
            disabled={templates.length === 0}
          >
            <option value="">テンプレートを選択</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.title}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleSendSelected}
            disabled={sending || selectedIds.size === 0}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {sending ? "送信中..." : "メール送信"}
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">送信対象一覧</h3>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-gray-500">読み込み中...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-500">{error}</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">対象データがありません。</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(event) => toggleSelectAll(event.target.checked)}
                      aria-label="select all"
                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">申込日</th>
                  <th className="px-4 py-3 text-left font-semibold">注文番号</th>
                  <th className="px-4 py-3 text-left font-semibold">ユーザー</th>
                  <th className="px-4 py-3 text-left font-semibold">資格</th>
                  <th className="px-4 py-3 text-left font-semibold">金額</th>
                  <th className="px-4 py-3 text-left font-semibold">支払方法</th>
                  <th className="px-4 py-3 text-left font-semibold">コース</th>
                  <th className="px-4 py-3 text-left font-semibold">本試験</th>
                  <th className="px-4 py-3 text-left font-semibold">メールアドレス</th>
                  <th className="px-4 py-3 text-left font-semibold">ON案内送信</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={(event) => toggleSelectOne(item.id, event.target.checked)}
                        aria-label={`select ${item.userEmail}`}
                        className="h-4 w-4 text-emerald-600 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-gray-700">{formatDate(item.createdAt)}</td>
                    <td className="px-4 py-3 text-gray-700">{item.orderNumber}</td>
                    <td className="px-4 py-3 text-gray-700">{item.userName}</td>
                    <td className="px-4 py-3 text-gray-700">{item.shikakuName}</td>
                    <td className="px-4 py-3 text-gray-700">{formatCurrency(item.totalAmount)}</td>
                    <td className="px-4 py-3 text-gray-700">{item.paymentMethod || "-"}</td>
                    <td className="px-4 py-3 text-gray-700">{item.course}</td>
                    <td className="px-4 py-3 text-gray-700">{item.hstatus || "-"}</td>
                    <td className="px-4 py-3 text-gray-700">{item.userEmail}</td>
                    <td className="px-4 py-3 text-gray-700">{item.oninfo || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 text-sm">
        <button
          type="button"
          onClick={handlePrevPage}
          disabled={loading || page <= 1}
          className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
        >
          前のページ
        </button>
        <span className="text-gray-500">{page}ページ目</span>
        <button
          type="button"
          onClick={handleNextPage}
          disabled={loading || !hasNextPage}
          className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
        >
          次のページ
        </button>
      </div>
    </div>
  );
}
